import { useState } from 'react'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import { Building2, Globe, Clock } from 'lucide-react'

const industries = [
  'Construction',
  'Healthcare',
  'Retail',
  'Manufacturing',
  'Hospitality',
  'Technology',
  'Education',
  'Transportation',
  'Other'
]

const timezones = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' }
]

export function CompanyDetailsStep() {
  const { data, updateData, nextStep, previousStep } = useOnboarding()
  const [errors, setErrors] = useState<Record<string, string>>({})
  
  const validate = () => {
    const newErrors: Record<string, string> = {}
    
    if (!data.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      nextStep()
    }
  }
  
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Tell us about your company
        </h2>
        <p className="text-gray-600">
          We'll use this information to personalize your experience
        </p>
      </div>
      
      <div className="space-y-6">
        {/* Company Name */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 mr-2" />
            Company Name *
          </label>
          <input
            type="text"
            value={data.companyName}
            onChange={(e) => updateData({ companyName: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.companyName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Acme Corporation"
          />
          {errors.companyName && (
            <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
          )}
        </div>
        
        {/* Industry */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Globe className="w-4 h-4 mr-2" />
            Industry (Optional)
          </label>
          <select
            value={data.industry || ''}
            onChange={(e) => updateData({ industry: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select an industry</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
        </div>
        
        {/* Timezone */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Clock className="w-4 h-4 mr-2" />
            Time Zone
          </label>
          <select
            value={data.timezone}
            onChange={(e) => updateData({ timezone: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            This will be used for scheduling and time tracking
          </p>
        </div>
      </div>
      
      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={previousStep}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Continue
        </button>
      </div>
    </form>
  )
}

export default CompanyDetailsStep